import { tracker } from 'libs/common/tracker';
import { checkConsentGroup } from 'libs/utils/consentGroups';
import { prebidTimedOutEvent } from 'libs/common/event-tracker/events';
import { ADS_BIDDER_TIMEOUT, Bidder, BidderState, MapSasFormatsToAdShape, SmartAdServerEventName } from 'constants/ads';
import { ConsentGroup } from 'constants/consent';
export function getPlacementById(id) {
  const {
    adPlacements
  } = window;
  return adPlacements === null || adPlacements === void 0 ? void 0 : adPlacements[id];
}
export function getAdUnitPath(_ref) {
  let {
    dfpAccountId,
    dfpCode
  } = _ref;
  return "/".concat(dfpAccountId, "/").concat(dfpCode);
}
export function getAdUnitName(_ref2) {
  let {
    shape,
    page,
    mediation
  } = _ref2;
  const adUnitNameParts = ['ad', shape, page];

  if (mediation) {
    adUnitNameParts.push(mediation);
  }

  return adUnitNameParts.join('-');
}
export function setupGoogleEvents(googleSlot, callbacks) {
  const {
    googletag
  } = window;
  if (!googletag) return;
  Object.keys(callbacks).forEach(callbackKey => {
    const key = callbackKey;
    const callback = callbacks[key];
    if (!callback) return;
    googletag.pubads().addEventListener(key, event => {
      if (event.slot === googleSlot) callback(event);
    });
  });
}
export function setupAdPlacement(_ref3) {
  let {
    id,
    placementConfig,
    node,
    isRefreshEnabled = false,
    isManuallyRendered = false,
    isManuallyRefreshed = false,
    isOutOfPageAd = false
  } = _ref3;

  if (!window.adPlacements) {
    window.adPlacements = {};
  }

  const {
    adPlacements
  } = window;
  const adUnitPath = getAdUnitPath(placementConfig);
  const adUnitName = getAdUnitName(placementConfig);
  adPlacements[id] = {
    code: id,
    adUnitPath,
    adUnitName,
    shape: placementConfig.shape,
    sizes: placementConfig.sizes,
    bids: placementConfig.bids,
    dfpCode: placementConfig.dfpCode,
    dfpAccountId: placementConfig.dfpAccountId,
    activeDuration: 0,
    isRefreshEnabled,
    isManuallyRendered,
    isManuallyRefreshed,
    isOutOfPageAd,
    node
  };
}
export function setupGoogleSlot(placementId, callbacks, iframeTitle) {
  const placement = getPlacementById(placementId);
  const {
    googletag
  } = window;
  if (!googletag || !placement) return;
  const {
    adUnitPath,
    sizes
  } = placement;
  googletag.cmd.push(() => {
    if (iframeTitle) googletag.setAdIframeTitle(iframeTitle);
    const slot = googletag.defineSlot(adUnitPath, sizes, placementId);
    if (!slot) return;
    slot.addService(googletag.pubads());
    slot.setCollapseEmptyDiv(true);
    setupGoogleEvents(slot, callbacks);
    placement.googleSlot = slot;
  });
}
export function handleSasNoAd(placementId, node) {
  const {
    pbjs
  } = window;
  if (!pbjs) return;
  const bid = pbjs.getHighestCpmBids(placementId)[0];

  if (bid && node) {
    const iframe = document.createElement('iframe');
    iframe.width = String(bid.width);
    iframe.height = String(bid.height);
    iframe.frameBorder = String(0);
    iframe.scrolling = 'no';
    node.appendChild(iframe);

    if (iframe.contentWindow) {
      pbjs.renderAd(iframe.contentWindow.document, bid.adId);
      iframe.contentWindow.document.body.style.padding = '0';
    }
  }
}
export function setupSasFormat(placementId, shape) {
  const placement = getPlacementById(placementId);
  const {
    sas
  } = window;
  if (!sas || !placement || !MapSasFormatsToAdShape[shape]) return;
  const {
    node
  } = placement;
  sas.cmd.push(() => {
    sas.events.on(SmartAdServerEventName.NoAd, () => handleSasNoAd(placementId, node), placementId);
    sas.adCallConfig.formats.push({
      id: MapSasFormatsToAdShape[shape],
      tagId: placementId
    });
  });
}
export function sendConsentlessAdRequest(placementId, bid) {
  const {
    sas
  } = window;
  if (!sas) return;
  if (bid) sas.setHeaderBiddingWinner(placementId, bid);
  sas.callSmart();
  sas.cmd.push(() => {
    sas.render(placementId);
  });
}
export function isBidderStateValid(requestManager, bidder) {
  return requestManager[bidder] === BidderState.Fetched;
}
export function sendConsentedAdserverRequest(placementId, requestManager) {
  const {
    googletag,
    pbjs,
    apstag
  } = window;
  const {
    isManuallyRendered
  } = getPlacementById(placementId);
  googletag.cmd.push(() => {
    const placementSlot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === placementId);
    if (isBidderStateValid(requestManager, Bidder.Prebid)) pbjs.setTargetingForGPTAsync([placementId]);
    if (isBidderStateValid(requestManager, Bidder.Amazon)) apstag.setDisplayBids();
    if (isManuallyRendered) googletag.display(placementId);
    if (placementSlot && !isManuallyRendered) googletag.pubads().refresh([placementSlot]);
  });
}
export function isArrayOfNumbers(candidate) {
  return !!candidate && Array.isArray(candidate) && !candidate.find(candidateItem => {
    return Array.isArray(candidateItem) ? !!candidateItem.find(candidateItemItem => Number.isNaN(Number.parseInt(candidateItemItem, 10))) : Number.isNaN(Number.parseInt(candidateItem, 10));
  });
}
export function fetchAmazonBids(placementId, onBidderBack) {
  let isRefresh = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const placement = getPlacementById(placementId);
  const {
    apstag
  } = window;
  let hasTimedOut = false;
  if (!apstag || !placement) return;
  const {
    sizes,
    dfpCode
  } = placement;

  if (sizes === 'fluid' || !isArrayOfNumbers(sizes)) {
    onBidderBack(Bidder.Amazon, placementId, BidderState.BadSizes);
    return;
  }

  const config = {
    slots: [{
      slotID: placementId,
      slotName: dfpCode,
      sizes
    }],
    params: {
      adRefresh: isRefresh ? '1' : '0'
    }
  };
  const timeout = setTimeout(() => {
    hasTimedOut = true;
    onBidderBack(Bidder.Amazon, placementId, BidderState.TimedOut);
  }, ADS_BIDDER_TIMEOUT);
  apstag.fetchBids(config, () => {
    if (hasTimedOut) return;
    clearTimeout(timeout);
    onBidderBack(Bidder.Amazon, placementId, BidderState.Fetched);
  });
}
export function fetchPrebidBids(placementId, onBidderBack) {
  var _placement$bids;

  let isRefresh = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const isTargetingEnabled = checkConsentGroup(ConsentGroup.Targeting);
  const placement = getPlacementById(placementId);
  const {
    pbjs
  } = window;
  if (!pbjs || !placement) return;

  if (!(placement !== null && placement !== void 0 && (_placement$bids = placement.bids) !== null && _placement$bids !== void 0 && _placement$bids.length)) {
    if (isTargetingEnabled) onBidderBack(Bidder.Prebid, placementId, BidderState.NoBids);
    return;
  }

  const {
    sizes,
    bids,
    adUnitName,
    adUnitPath,
    googleSlot
  } = placement;
  const prebidPlacement = {
    code: placementId,
    mediaTypes: {
      banner: {
        sizes
      }
    },
    bids: bids.reduce((acc, bid) => {
      const stringifiedBid = JSON.stringify(bid);
      const stringifiedBids = JSON.stringify(acc);
      if (stringifiedBids.includes(stringifiedBid)) return acc;
      return [...acc, bid];
    }, []),
    pubstack: {
      adUnitPath,
      adUnitName
    }
  };
  pbjs.que.push(() => {
    if (!isRefresh) pbjs.addAdUnits(prebidPlacement);
    pbjs.requestBids({
      adUnitCodes: [placementId],

      bidsBackHandler(_bids, timedOut) {
        if (timedOut) tracker.track(prebidTimedOutEvent());

        if (isTargetingEnabled) {
          if (isRefresh) googleSlot === null || googleSlot === void 0 ? void 0 : googleSlot.setTargeting('ad_refresh', 'true');
          if (timedOut) return onBidderBack(Bidder.Prebid, placementId, BidderState.TimedOut);
          return onBidderBack(Bidder.Prebid, placementId, BidderState.Fetched);
        }

        const bid = pbjs.getHighestCpmBids(placementId)[0];
        return sendConsentlessAdRequest(placementId, bid);
      },

      timeout: ADS_BIDDER_TIMEOUT
    });
  });
}